import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { setDrivers } from "../../../store/actions/project";
import { setExtendableType } from "../../../store/actions/edit";
import "../../../customizedLibs/phoneInput/style/style.css";
import { Link as RouterLink, useHistory } from "react-router-dom";
import noDriversAdded from "../../../assets/images/no-drivers-added.svg";
import { Box, makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Link from "@material-ui/core/Link";
import API from "../../../utils/axios";
import { useSnackbar } from "notistack";
import DriverDragItem from "./DriverDragItem";
import { setInfoDialog } from "../../../store/actions/common";
import { exportToXlsx, remCalc } from "../../../utils/helpers";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import SearchBox from "../../common/SearchBox";
import { Droppable } from "react-beautiful-dnd";

const useStyles = makeStyles(theme => ({
  header: {
    borderBottom: theme.variables.border,
    minHeight: 79,
    boxSizing: "border-box"
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500,
    marginBottom: theme.spacing(0.5)
  },
  subtitle: {
    fontSize: "0.875rem",
    lineHeight: "1rem"
  },
  body: {
    maxHeight: "100%",
    overflow: "auto",

    [theme.breakpoints.down("md")]: {
      paddingRight: theme.spacing(3)
    }
  },

  noDriversText: {
    marginTop: theme.spacing(5),
    textAlign: "center",
    fontSize: remCalc(15),
    "& a": {
      cursor: "pointer",
      fontWeight: 500,
      textDecoration: "none !important",
      "&:hover": {
        textDecoration: "underline !important"
      }
    }
  }
}));

function DriversList({
  drivers,
  setExtendableType,
  setDrivers,
  plan,
  setInfoDialog,
  solution,
}) {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState("");
  const [filteredDrivers, setFilteredDrivers] = useState(drivers);

  const checkAssigned = id => {
    return (
      solution.routes.findIndex(
        route => route.driver && route.driver.id === id
      ) !== -1
    );
  };

  const history = useHistory();

  const handleChange = e => {
    let { value } = e.target;

    setInputValue(value);
    value = value.toLowerCase();
    const result = drivers.filter(driver => {
      return (
        (driver.first_name + " " + driver.last_name)
          .toLowerCase()
          .indexOf(value) !== -1 ||
        driver.email.toLowerCase().indexOf(value) !== -1
      );
    });
    setFilteredDrivers(result);
  };

  useEffect(() => {
    setFilteredDrivers(drivers);
    setInputValue("");
  }, [drivers]);

  // Close edit panel
  const handleClose = () => {
    setExtendableType(null);
  };

  // Open edit panel for driver
  const editDriver = driver => {
    history.push("/drivers", {
      action: "edit-driver",
      driverId: driver.id
    });
  };

  const { enqueueSnackbar } = useSnackbar();

  // Delete driver
  const deleteDriver = id => {
    API.delete(`/v1/drivers/${id}`)
      .then(res => {
        setDrivers(drivers.filter(el => el.id !== id));
      })
      .catch(error => {
        enqueueSnackbar("Something went wrong, couldn't delete driver");
        console.log(error);
      });
  };

  const [selected, setSelected] = useState([]);

  const handleSelect = id => {
    if (selected.includes(id)) {
      setSelected(selected.filter(selectedId => selectedId !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const handleMultipleDownload = selected => {
    exportToXlsx("selected_drivers.xlsx", [
      ["first_name", "last_name", "email", "mobile_number", "driver_tags"],
      ...drivers
        .filter(driver => selected.includes(driver.id))
        .map(driver => [
          driver.first_name,
          driver.last_name,
          driver.email,
          driver.mobile_number,
          driver.tags.map(el => el.name).join("; ")
        ])
    ]);
  };

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const newSelected = filteredDrivers.map(n => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      data-cy="driversExtendablePanel"
    >
      <Box
        className={classes.header}
        display="flex"
        alignItems="start"
        justifyContent="space-between"
        px={3}
        py={1.5}
      >
        <Box
          width="100%"
          alignSelf="center"
          justifyContent="space-between"
          display="flex"
          pr={2}
        >
          <Box>
            <Typography className={classes.title} variant="h5">
              Drivers
            </Typography>

            <Typography className={classes.subtitle} color="secondary">
              Drag-and-drop to assign to route
            </Typography>
          </Box>
        </Box>

        <OptimizedTooltip placement="bottom" title="Close">
          <Box mr={-1} mt={0.5}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </OptimizedTooltip>
      </Box>

      {!!drivers.length && (
        <Box pt={3} pl={3} pr={4} pb={3}>
          <SearchBox
            label="driver"
            data={drivers}
            value={inputValue}
            filteredData={filteredDrivers}
            selected={selected}
            handleChange={handleChange}
            handleSelectAllClick={handleSelectAllClick}
            handleMultipleDownload={handleMultipleDownload}
          />
        </Box>
      )}

      <Box
        className={classes.body}
        display="flex"
        flexDirection="column"
        flex="1 1 100%"
        pr={!drivers.length ? 3 : 4}
        pt={0}
        pb={1}
        pl={3}
      >
        {!drivers.length && (
          <Box
            pt={4.75}
            display="flex"
            alignItems="center"
            flexDirection="column"
          >
            <img src={noDriversAdded} alt="" />
            <Typography className={classes.noDriversText} color="secondary">
              Looks like you haven’t added any drivers yet! <br /> Start by{" "}
              <Link component={RouterLink} to="/drivers">
                adding a driver
              </Link>{" "}
              or simply{" "}
              <Link
                onClick={e => {
                  if (plan.ft_spreadsheet_upload) {
                    history.push(`/drivers/upload`);
                  } else {
                    setInfoDialog({
                      visible: true,
                      title: "Please upgrade your plan",
                      body: "Spreadsheet importing is not included in the Individual plan, please upgrade your plan to Small Business.",
                      button: {
                        text: "Upgrade plan",
                        link: "/payment/select-plan"
                      },
                      mainButtonText: "No, thanks"
                    });
                  }
                }}
              >
                upload a list
              </Link>
              .
            </Typography>
          </Box>
        )}

        <Box pb={4} data-cy="driversListExtendable">
          <Droppable droppableId="drivers" isDropDisabled>
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ width: "100%" }}
              >
                {filteredDrivers.map((item, index) => {
                  return (
                    <DriverDragItem
                      item={item}
                      key={item.id}
                      isSelected={selected.includes(item.id)}
                      handleSelect={handleSelect}
                      inputValue={inputValue}
                      checkAssigned={checkAssigned}
                      index={index}
                      deleteDriver={deleteDriver}
                      editDriver={editDriver}
                    />
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </Box>
      </Box>
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    drivers: state.project.drivers || [],
    solution: state.project.solution,

    plan:
      (state.auth.userData.organisationSite &&
        state.auth.userData.organisationSite.organisation.plan) ||
      {}
  };
}

export default connect(mapStateToProps, {
  setExtendableType,
  setDrivers,
  setInfoDialog
})(DriversList);
